import { useNavigate, useParams } from 'react-router';
import { useSearchParams } from 'react-router-dom';
import { Button } from 'flowbite-react';
import { useEffect, useRef } from 'react';
import { enqueueSnackbar } from 'notistack';
import useReturnToAppInterstitial from 'hooks/useReturnToAppInterstitial';
import IndicatorPageLoader from 'navigation/loaders/Indicator';
import BackArrowButtonIcon from 'assets/svg/BackArrowButton';
import CustomerInfo from 'components/preview/CustomerInfo';
import StatusBadge from 'components/StatusBadge';
import { useGetOrderDetailsQuery } from 'api/queries/product/get-order';
import { AimeosProductType } from 'api/types';
import { HorizontalDashedDelimiter } from 'components/DashedDelimiter';
import { OrderStatus } from 'utils/enums';
import { useGetEscrowByTradeQuery } from 'api/queries/escrow/get-escrow-by-trade';
import { hasOnlyTerminalDisputes } from 'utils/escrow-helpers';
import SectionSeparator from 'pages/client/Orders/Make/components/SectionSeparator';
import BoxRoundedIcon from 'assets/svg/BoxRounded';
import CoinsRoundedIcon from 'assets/svg/CoinsRounded';
import { useModals } from 'providers/Modals';
import Actions from './components/Actions';
import DeliveryPanel from './components/DeliveryPanel';
import Preview from './components/Preview';
import EscrowPanel from './components/EscrowPanel';

/**
 * Fallback duration in milliseconds before clearing `?fromPayment=success` from the URL
 * when `escrowEnabled` never becomes true (e.g. unexpected trade status or 404'd trade).
 * Exported so tests can reference the same constant without magic numbers.
 */
export const PAYMENT_SYNC_FALLBACK_MS = 10_000;

/**
 * Trade details page.
 *
 * Handles the `?fromPayment=success|cancel` query parameter injected by the payment
 * redirector pages. On success, waits for escrow data to load before showing the
 * payment-status modal — the gateway sync itself is handled automatically by the
 * backend on the next GET of the escrow endpoint (see
 * `docs/2026-02-20-backend-escrow-api-guide.md` §4.1). On cancel, shows the
 * modal immediately.
 */
export default function TradeDetailsPage() {
  const navigate = useNavigate();
  const goBack = () => navigate(-1);

  const { tradeId = '' } = useParams<{ tradeId: string }>();
  const [searchParams, setSearchParams] = useSearchParams();

  // accept both ?fromPayment= (frontend redirectors) and ?payment= (backend/gateway direct)
  // normalise 'failure' → 'cancel' for the modal variant
  const rawFromPayment = searchParams.get('fromPayment') ?? searchParams.get('payment');
  const fromPayment: 'success' | 'cancel' | null =
    rawFromPayment === 'success'
      ? 'success'
      : rawFromPayment === 'cancel' || rawFromPayment === 'failure'
        ? 'cancel'
        : null;

  // detect external mobile browser after a payment redirect
  const { active: interstitialActive, element: interstitialElement } = useReturnToAppInterstitial(fromPayment);

  const syncTriggered = useRef(false);
  const { open: openModal } = useModals();

  const { data, isFetching } = useGetOrderDetailsQuery(
    { orderId: tradeId, productType: AimeosProductType.Trade },
    false,
  );

  const tradeStatus = data?.trade?.tradeStatus;
  const showEscrowPanel = [
    OrderStatus.PendingFunding,
    OrderStatus.Traded,
    OrderStatus.DisputeOpen,
    OrderStatus.Completed,
  ].includes(tradeStatus as OrderStatus);

  // extracted to share the condition between the hook and the sync effect
  const escrowEnabled = showEscrowPanel && !!tradeId;

  const {
    data: escrow,
    isError: isEscrowError,
    error: escrowError,
  } = useGetEscrowByTradeQuery(+tradeId, {
    enabled: escrowEnabled && !fromPayment,
  });

  // One-off handler for returning from the payment gateway.
  // success: wait for escrowEnabled, show modal immediately, then sync in background.
  // cancel:  show modal immediately — no sync needed as no payment was made.
  useEffect(() => {
    if (!fromPayment || syncTriggered.current) return;

    // defer modal logic while the return-to-app interstitial is visible
    if (interstitialActive) return;

    /**
     * Opens the payment-status modal for the current variant and removes
     * `fromPayment` from the URL so the effect does not re-fire.
     */
    const showModalAndClean = () => {
      openModal('payment-status', {
        variant: fromPayment,
        onRetryPayment:
          fromPayment === 'cancel'
            ? () => {
                setSearchParams(
                  (prev) => {
                    const next = new URLSearchParams(prev);
                    next.set('action', 'pay');
                    return next;
                  },
                  { replace: true },
                );
              }
            : undefined,
      });
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          next.delete('fromPayment');
          next.delete('payment');
          return next;
        },
        { replace: true },
      );
    };

    if (fromPayment === 'success') {
      // wait for escrowEnabled — effect re-fires when it changes; do NOT set syncTriggered yet
      if (!escrowEnabled) return;
      syncTriggered.current = true;

      // Show modal and clean URL. Removing `fromPayment` from the URL re-enables
      // the main useGetEscrowByTradeQuery hook below, whose next fetch will
      // auto-sync the payment state at the backend — no explicit sync call is
      // needed from the frontend.
      showModalAndClean();
    } else {
      // cancel: no sync required — show modal without waiting for escrow data
      syncTriggered.current = true;
      showModalAndClean();
    }
  }, [fromPayment, escrowEnabled, tradeId, setSearchParams, openModal, interstitialActive]);

  // Fallback: clear fromPayment if escrowEnabled never becomes true within PAYMENT_SYNC_FALLBACK_MS.
  // Prevents the param from remaining stuck when the trade is in an unexpected status.
  useEffect(() => {
    if (fromPayment !== 'success' || syncTriggered.current) return;

    // defer fallback timer while the return-to-app interstitial is visible
    if (interstitialActive) return;

    const fallbackTimeout = setTimeout(() => {
      if (!syncTriggered.current) {
        syncTriggered.current = true;

        // warn the user that the escrow status could not be confirmed automatically
        enqueueSnackbar('Could not sync payment status. Please refresh the page.', { variant: 'warning' });

        // still show the modal so the user has persistent acknowledgement of their payment
        openModal('payment-status', { variant: fromPayment });

        setSearchParams(
          (prev) => {
            const next = new URLSearchParams(prev);
            next.delete('fromPayment');
            next.delete('payment');
            return next;
          },
          { replace: true },
        );
      }
    }, PAYMENT_SYNC_FALLBACK_MS);

    return () => clearTimeout(fallbackTimeout);
  }, [fromPayment, openModal, setSearchParams, interstitialActive]);

  const hasValidData = !!data?.trade && !!data?.order;

  useEffect(() => {
    if (!isFetching) {
      if (!hasValidData) {
        const timeout = setTimeout(() => navigate('/404', { replace: true }), 1000);

        return () => clearTimeout(timeout);
      }
    }
  }, [isFetching, hasValidData, navigate]);

  useEffect(() => {
    const status = (escrowError as { response?: { status?: number } })?.response?.status;
    if (status === 403) {
      navigate('/404', { replace: true });
    }
  }, [escrowError, navigate]);

  // show return-to-app interstitial when in external mobile browser after payment redirect
  if (interstitialElement) return interstitialElement;

  if (isFetching || !data?.trade || !data?.order) {
    return <IndicatorPageLoader fullPage />;
  }

  return (
    <div className="flex flex-col p-4 md:p-10">
      <div className="flex flex-col md:flex-row justify-between md:items-center md:flex-wrap gap-2">
        <div className="flex gap-2 lg:gap-4 items-center">
          <Button
            pill
            color="light"
            className="font-semibold border-0 bg-brand-purple-light hidden md:block"
            onClick={goBack}
          >
            <span className={'hidden md:inline'}>Back</span>
          </Button>

          <button className={'hover:shadow-md md:hidden'} onClick={goBack}>
            <BackArrowButtonIcon size={26} />
          </button>

          <h2 className={'whitespace-pre-wrap text-xl md:text-[32px] leading-normal font-black text-brand-black'}>
            {data.trade.orderType} Order <span className={'font-normal break-words'}>{data.order?.code}</span>
          </h2>

          <div className={'ml-auto md:hidden'} />

          <StatusBadge status={data.trade.tradeStatus} />
        </div>

        <HorizontalDashedDelimiter strokeLinecap={'round'} strokeDasharray={'0.1 6'} className={'md:hidden mt-1'} />

        <CustomerInfo userId={data?.order?.owner || ''} navigateToProfile />

        <HorizontalDashedDelimiter strokeLinecap={'round'} strokeDasharray={'0.1 6'} className={'md:hidden'} />
      </div>

      <div className={'h-4 md:h-12'} />

      {tradeStatus === OrderStatus.DisputeOpen && escrow?.disputes && (
        <div
          className={`rounded-lg p-3 mb-4 text-sm font-medium ${
            hasOnlyTerminalDisputes(escrow.disputes)
              ? 'bg-green-50 text-green-800 border border-green-200'
              : 'bg-amber-50 text-amber-800 border border-amber-200'
          }`}
        >
          {hasOnlyTerminalDisputes(escrow.disputes)
            ? 'All disputes have been resolved. You may now complete the trade.'
            : 'A dispute is currently open on this trade. Actions are limited until it is resolved.'}
        </div>
      )}

      {tradeStatus === OrderStatus.TerminatedPostActivation && (
        <div className="rounded-lg p-3 mb-4 text-sm font-medium bg-red-50 text-red-800 border border-red-200">
          This trade has been terminated after escrow activation. Funds will be handled according to the resolution
          outcome.
        </div>
      )}

      {escrow?.closedAt && !escrow?.activatedAt && (
        <div className="rounded-lg p-3 mb-4 text-sm font-medium bg-gray-50 text-gray-700 border border-gray-200">
          Escrow was canceled before activation. No funds were collected.
        </div>
      )}

      {isEscrowError && (escrowError as { response?: { status?: number } })?.response?.status !== 404 && (
        <div className="rounded-lg p-3 mb-4 text-sm font-medium bg-amber-50 text-amber-800 border border-amber-200">
          Unable to fetch latest escrow status. Please refresh the page.
        </div>
      )}

      <Preview {...data.trade!} currency={escrow?.currency} />

      <div className={'h-8 md:h-12'} />

      <div className={'grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-12'}>
        {showEscrowPanel && (
          <div>
            <SectionSeparator Icon={CoinsRoundedIcon}>Escrow Status</SectionSeparator>
            <div className={'h-5 md:h-8'} />
            <EscrowPanel tradeId={+data.trade.id} tradeStatus={data.trade.tradeStatus} />
          </div>
        )}

        <div>
          <SectionSeparator Icon={BoxRoundedIcon}>Delivery</SectionSeparator>
          <div className={'h-5 md:h-8'} />
          <DeliveryPanel {...data.trade!} />
        </div>
      </div>

      <div className={'h-7 md:h-16'} />

      <Actions trade={data.trade} order={data.order} />

      <div className={'h-8'} />
    </div>
  );
}
